import {
  ItineraryDayV2,
  ItineraryItemV2,
  ItineraryV2,
  ItineraryV2User,
} from "@/types/itinerary/itineraryV2";
import { PlacesBaseOnCity } from "@/types/itinerary";
import { WebbedHotel } from "@/types/webbeds";
import { create, StateCreator } from "zustand";
import { slugifyOptions } from "@/utils/slugifyOptions";
import slugify from "slugify";
import { TopAttractionV2 } from "@/types/itinerary/attractions";

export interface ItinerarySliceV2 {
  settings: { visibility: "private" | "public"; title: string };
  itinerary: ItineraryV2 | null;
  itineraryDays: ItineraryDayV2[];
  topAttractions: TopAttractionV2[];
  activeDays: number[];
  showActivities: boolean;
  showHotels: boolean;
  pathDayIndex: number | null;
  pathPoints: { lat: number | string; lng: number | string }[];
  modalData: ItineraryItemV2 | null;
  itineraryId: string;
  baseUrl: string;
  showAccessModal: boolean;
  showMap: boolean;
  itemsDetails: PlacesBaseOnCity[];
  showPlaceDetail: PlacesBaseOnCity | null;
  activityModal: PlacesBaseOnCity | null;
  hotelDetail: WebbedHotel | null;
  placesModal: PlacesBaseOnCity | null;
  zoomTo: { lat: number; lng: number } | null;

  setItineraryId: (id: string) => void;
  setModalData: (data: ItineraryItemV2 | null) => void;
  setItinerary: (itinerary: ItineraryV2) => void;
  setPathDayIndex: (pathDayIndex: number | null) => void;
  setPathPoints: (
    pathPoints: { lat: number | string; lng: number | string }[],
  ) => void;
  setItineraryDays: (days: ItineraryDayV2[]) => void;
  addItineraryDay: (day: ItineraryDayV2) => void;
  updateItineraryDay: (
    date: string,
    updatedDay: Partial<ItineraryDayV2>,
  ) => void;
  clearItinerary: () => void;
  setActiveDays: (days: number[]) => void;
  setShowActivities: (show: boolean) => void;
  setShowHotels: (show: boolean) => void;
  setShowAccessModal: (show: boolean) => void;
  fetchItineraryById: (id: string) => Promise<void>;
  setShowMap: (show: boolean) => void;
  setItemsDetails: (items: PlacesBaseOnCity[]) => void;
  setShowPlaceDetail: (show: PlacesBaseOnCity | null) => void;
  setActivityModal: (show: PlacesBaseOnCity | null) => void;
  setHotelDetail: (show: WebbedHotel | null) => void;
  setPlacesModal: (show: PlacesBaseOnCity | null) => void;
  setZoomTo: (show: { lat: number; lng: number } | null) => void;
  setBaseUrl: (url: string) => void;
  setTopAttractions: (attractions: TopAttractionV2[]) => void;
  updateItinerary: (
    data: ItineraryV2,
    topAttractions?: TopAttractionV2[],
  ) => void;
  setSettings: (settings: {
    visibility?: "private" | "public";
    title?: string;
  }) => void;
}

export const createItinerarySlice: StateCreator<ItinerarySliceV2> = (set) => ({
  settings: { visibility: "private", title: "" },
  itinerary: null,
  itineraryDays: [],
  topAttractions: [],
  itineraryId: "",
  activeDays: [],
  showActivities: true,
  showHotels: true,
  pathDayIndex: null,
  pathPoints: [],
  modalData: null,
  baseUrl: "",
  showAccessModal: false,
  showMap: false,
  itemsDetails: [],
  showPlaceDetail: null,
  activityModal: null,
  hotelDetail: null,
  placesModal: null,
  zoomTo: null,
  setItineraryId: (id) => set({ itineraryId: id }),
  setSettings: (updatedSettings) =>
    set((state) => ({
      settings: { ...state.settings, ...updatedSettings },
    })),
  setModalData: (data) => set({ modalData: data }),
  setItinerary: (itinerary) => set({ itinerary }),
  setPathDayIndex: (pathDayIndex) => set({ pathDayIndex }),
  setPathPoints: (pathPoints) => set({ pathPoints }),
  setItineraryDays: (days) => set({ itineraryDays: days }),
  addItineraryDay: (day) =>
    set((state) => ({ itineraryDays: [...state.itineraryDays, day] })),
  updateItineraryDay: (date, updatedDay) =>
    set((state) => ({
      itineraryDays: state.itineraryDays.map((day) =>
        day.date === date ? { ...day, ...updatedDay } : day,
      ),
    })),
  clearItinerary: () => set({ itinerary: null, itineraryDays: [] }),
  setActiveDays: (days) => set({ activeDays: days }),
  setShowActivities: (show) => set({ showActivities: show }),
  setShowHotels: (show) => set({ showHotels: show }),
  setShowAccessModal: (show) => set({ showAccessModal: show }),
  setShowMap: (show) => set({ showMap: show }),
  setItemsDetails: (items) => set({ itemsDetails: items }),
  setShowPlaceDetail: (show) => set({ showPlaceDetail: show }),
  setActivityModal: (show) => set({ activityModal: show }),
  setHotelDetail: (show) => set({ hotelDetail: show }),
  setPlacesModal: (show) => set({ placesModal: show }),
  setZoomTo: (show) => set({ zoomTo: show }),
  setBaseUrl: (url) => set({ baseUrl: url }),
  setTopAttractions: (attractions) => set({ topAttractions: attractions }),
  updateItinerary: (data, topAttractions) =>
    set({
      settings: { title: data.title, visibility: data.visibility },
      itinerary: data,
      itineraryDays: data.days || [],
      itineraryId: data.id || "",
      baseUrl: `/itineraries/${slugify(data?.country || "country", slugifyOptions)}/${slugify(data?.city || "area", slugifyOptions)}/${data.id}`,
      ...(topAttractions ? { topAttractions } : {}),
    }),
  fetchItineraryById: async (id: string) => {
    try {
      const res = await fetch(`/api/itinerary/${id}`);
      const { result: itinerary } = await res.json();
      set({ itinerary });
    } catch (error) {
      console.error("Failed to fetch itinerary:", error);
    }
  },
});
